import * as moment from 'moment';
import * as uuid from 'uuid';

import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IonModal, ToastController } from '@ionic/angular';
import { OverlayEventDetail } from '@ionic/core/components';
import { BasePage } from '@pages/base.page';
import { DefaultObject } from '@shared/interfaces';

@Component({
	selector: 'manifest-form-modal',
	template: `
		<ion-modal (willDismiss)="onWillDismiss($event)">
			<ng-template>
				<ion-header>
					<ion-toolbar>
						<ion-buttons slot="start">
							<ion-button (click)="close()">Cancel</ion-button>
						</ion-buttons>
						<ion-title *ngIf="reservationLaunchType === 'launch'"
							>Guest/Minor</ion-title
						>
						<ion-title *ngIf="reservationLaunchType === 'checkin'"
							>Operator/Guest</ion-title
						>
					</ion-toolbar>
				</ion-header>
				<ion-content class="ion-padding">
					<form [formGroup]="form" errorTailor (ngSubmit)="saveForm(form)">
						<ion-list class="mb-5">
							<ion-item>
								<ion-label position="floating" color="primary">Name</ion-label>
								<ion-input
									class="capitalize"
									inputError
									formControlName="name"
									type="text"
									placeholder="Your Name"
								></ion-input>
							</ion-item>
							<ion-item>
								<ion-label position="floating" color="primary"
									>Email<sup>1</sup></ion-label
								>
								<ion-input
									inputError
									formControlName="email"
									type="email"
									placeholder="Your Email"
								></ion-input>
							</ion-item>
							<ion-item *ngIf="reservationLaunchType === 'checkin' && isDriver">
								<ion-label position="floating" color="primary">DOB</ion-label>
								<ion-input
									inputError
									formControlName="dob"
									type="text"
									placeholder="Your DOB"
									readonly
									(click)="handleDateTime()"
								></ion-input>
							</ion-item>
							<ion-item *ngIf="reservationLaunchType === 'checkin'">
								<ion-label color="primary">Is an Operator<sup>2</sup></ion-label>
								<ion-toggle
									[disabled]="form.get('isMinor').value"
									[checked]="passenger?.isDriving"
									(ionChange)="driverToggle($event)"
								></ion-toggle>
							</ion-item>

							<ion-item>
								<ion-label color="primary">Is a Minor</ion-label>
								<ion-toggle
									[disabled]="form.get('isDriving').value"
									[checked]="passenger?.isMinor"
									(ionChange)="minorToggle($event)"
								></ion-toggle>
							</ion-item>
						</ion-list>
						<ion-button
							expand="block"
							type="submit"
							[strong]="true"
							[disabled]="form.invalid"
							>Save
						</ion-button>
					</form>
					<p *ngIf="this.isDriver" class="text-xs mt-2 p-2 text-red-600">
						If you elect to purchase insurance for this trip, adding each additional
						operator will increase the insurance premium by $10.00.
					</p>
					<p class="text-xs mt-2 p-2">
						<sup>1</sup>If you want to have your signed forms/waivers emailed to you
						after completion, please input your email address. If no email is added then
						signed documents will not be sent
					</p>
					<p class="text-xs mt-2 p-2">
						<sup>2</sup>Email/DOB is required if Guest is an Operator
					</p>
					<date-picker
						[preferWheel]="true"
						[allowAllDates]="true"
						*ngIf="showDate"
						(onDateChange)="onDateChange($event)"
					>
					</date-picker>
				</ion-content>
			</ng-template>
		</ion-modal>
	`,
})
export class ManifestFormComponent extends BasePage implements OnInit {
	public form: FormGroup;
	public passenger: DefaultObject = {};
	public isLoading: boolean = true;
	public showDate: boolean = false;
	public isDriver: boolean = false;
	@ViewChild(IonModal) modal: IonModal;
	@Input() reservationLaunchType: string;
	@Output() onModalClose: EventEmitter<any> = new EventEmitter();
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - constructor
	 **-------------------------------------------------------------------------------------
	 */
	constructor(
		private readonly formBuilder: FormBuilder,
		public readonly toastCtrl: ToastController
	) {
		super();
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - ngOnInit
	 **-------------------------------------------------------------------------------------
	 */
	ngOnInit() {}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - onWillDismiess
	 **-------------------------------------------------------------------------------------
	 */
	onWillDismiss(event: Event) {
		const ev = event as CustomEvent<OverlayEventDetail<string>>;
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - create
	 **-------------------------------------------------------------------------------------
	 */
	public create(passenger) {
		this.passenger = passenger;
		this.form = this.formBuilder.group({
			name: [passenger?.name, [Validators.required]],
			email: [passenger?.email, [Validators.email]],
			dob: [
				passenger?.dob ? moment(passenger?.dob).format('MM/DD/YYYY') : null,
				passenger?.isDriving ? [Validators.required] : [],
			],
			isDriving: [passenger?.isDriving],
			isMinor: [passenger?.isMinor],
		});

		this.isLoading = false;
		return this;
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - open
	 **-------------------------------------------------------------------------------------
	 */
	public present() {
		this.modal.present();
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - cancel
	 **-------------------------------------------------------------------------------------
	 */
	public close() {
		this.modal.dismiss(null, 'close');
		this.isDriver = false;
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - saveForm
	 **-------------------------------------------------------------------------------------
	 */
	public async saveForm({ value }) {
		if (value.isDriving) {
			const yearsSince = moment().diff(value.dob, 'years');
			if (yearsSince < 18 && value.isDriving === true) {
				await this.alert({
					message: 'This passenger cannot drive since they are under the age of 18',
				});
				return;
			}
		}
		const saveType = this.passenger?.uuid ? 'update' : 'insert';
		this.onModalClose.emit({
			passenger: {
				...value,
				uuid: this.passenger?.uuid || uuid.v4(),
				type: this.getType(value),
			},
			type: saveType,
		});
		this.toastCtrl
			.create({
				message: value.isDriving ? 'Operator Added' : 'Passenger Added',
				duration: 3000,
				position: 'bottom',
				color: 'success',
			})
			.then(toast => {
				toast.present();
			});
		this.isDriver = false;
		await this.modal.dismiss(null, 'close');
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - toggle
	 **-------------------------------------------------------------------------------------
	 */
	public driverToggle({ detail }): void {
		this.isDriver = !this.isDriver;
		if (detail.checked) {
			this.form.get('email').setValidators([Validators.required, Validators.email]);
			this.form.get('email').updateValueAndValidity();
			this.form.get('dob').setValidators([Validators.required]);
			this.form.get('dob').updateValueAndValidity();
			this.form.get('isDriving').setValue(true);
		}
		if (!detail.checked) {
			this.form.get('email').setValidators([Validators.email]);
			this.form.get('email').updateValueAndValidity();
			this.form.get('dob').setValidators([]);
			this.form.get('dob').updateValueAndValidity();
			this.form.get('isDriving').setValue(false);
		}
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - toggle
	 **-------------------------------------------------------------------------------------
	 */
	public minorToggle({ detail }): void {
		if (detail.checked) {
			this.form.get('isMinor').setValue(true);
		}
		if (!detail.checked) {
			this.form.get('isMinor').setValue(false);
		}
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getType
	 **-------------------------------------------------------------------------------------
	 */
	public getType(value) {
		if (value.isDriving) return 'additional_operator';
		else if (value.isMinor) return 'minor';
		else return 'passenger';
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - onDateChange
	 **-------------------------------------------------------------------------------------
	 */
	public async onDateChange(value) {
		this.form.get('dob').setValue(moment(value).format('MM/DD/YYYY'));
		this.showDate = false;
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - handleDateTime
	 **-------------------------------------------------------------------------------------
	 */
	public async handleDateTime() {
		this.showDate = true;
	}
}
